import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
  LinearProgress,
  Alert,
  Chip,
  Grid
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  FileUpload as FileUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { saveFormDetails } from "../redux/slices/CreateAgentSlice";
import { statusAiAgent } from "../redux/slices/StatusProgressSlice";
import { saveTaskId } from "../redux/slices/DeployAIAgent";
import { apiUploadFiles } from "../api/apiclient";
import { AI_AGENT_UPLOAD } from "../api/endpoints";

// Custom hooks
import { useFileUpload } from './upload/hooks/useFileUpload';
import { useDragAndDrop } from './upload/hooks/useDragAndDrop';

const UploadPopup = ({ open, onClose, slugName }) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadResponseData, setUploadResponseData] = useState(null);

  const setFormDetailsValue = useCallback((key, value) => {
    dispatch(saveFormDetails({ key, value }));
  }, [dispatch]);

  const {
    uploadedFiles,
    uploading,
    uploadProgress,
    error,
    addFiles,
    removeFile,
    setError,
    setUploading,
    setUploadedFiles
  } = useFileUpload();

  const handleFilesAdded = useCallback((files) => {
    addFiles(files);
  }, [addFiles]);

  const { dragActive, handleDrag, handleDrop } = useDragAndDrop(handleFilesAdded);

  const handleFileSelect = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback((e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0]; // Only take the first file
      handleFilesAdded([file]);
    }
  }, [handleFilesAdded]);

  const handleUpload = useCallback(async () => {
    if (uploadedFiles.length === 0) {
      setError('Please select a file to upload');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('files', uploadedFiles[0].file);
      const uploadUrl = `${AI_AGENT_UPLOAD}${slugName}/upload/`;

      // Use fetch API directly for this specific endpoint
      const accessToken = localStorage.getItem("access_token");
      const url = `${process.env.REACT_APP_BASE_URL || 'http://127.0.0.1:8000/api/'}${uploadUrl}`;

      const requestHeaders = {};
      if (accessToken) {
        requestHeaders.Authorization = `Bearer ${accessToken}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: requestHeaders,
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Upload failed:', errorData);
        const errorMessage = errorData?.data?.message || errorData?.message || `Upload failed: ${response.status} ${response.statusText}`;
        throw new Error(errorMessage);
      }

      const responseData = await response.json();

      // Debug: Log the full response structure
      console.log('Full API Response:', responseData);
      console.log('Response data structure:', responseData.data);

      if (responseData?.status === 'success') {
        // Store the uploaded file data - the actual data is nested under data.data
        const uploadData = responseData.data?.data || responseData.data;
        console.log('Extracted upload data:', uploadData);

        // Validate that we have the required fields
        if (uploadData && (uploadData.uploaded_file || uploadData.slug || uploadData.task_id)) {
          setFormDetailsValue('uploadedFile', uploadData);
          setUploadResponseData(uploadData);
          setUploadSuccess(true);
          console.log('File uploaded successfully:', uploadData);
        } else {
          console.error('Invalid upload data structure:', uploadData);
          setError('Upload completed but received invalid response data');
        }
      } else {
        setError('Upload failed. Please try again.');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  }, [uploadedFiles, setUploading, setError, setFormDetailsValue, setUploadedFiles, slugName]);

  const handleClose = useCallback(() => {
    setUploadedFiles([]);
    setError(null);
    setUploadSuccess(false);
    setUploadResponseData(null);
    onClose();
  }, [setUploadedFiles, onClose]);

  const handleThankYou = useCallback(() => {
    setUploadSuccess(false);
    setUploadResponseData(null);
    setUploadedFiles([]);
    setError(null);
    onClose();
    // Refresh the page
    window.location.reload();
  }, [onClose]);

  const handleStartStatusPolling = useCallback((taskId) => {
    if (!taskId) {
      console.error('No taskId provided for status polling');
      return;
    }

    console.log('Starting status polling with taskId:', taskId);

    // Save the taskId to Redux state so the parent component can use it
    dispatch(saveTaskId(taskId));

    // Start polling the status immediately
    dispatch(statusAiAgent(taskId));

    // Close the upload popup
    setUploadSuccess(false);
    setUploadResponseData(null);
    setUploadedFiles([]);
    setError(null);
    onClose();
  }, [dispatch, onClose]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            overflow: 'hidden',
            maxWidth: '500px'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 2.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CloudUploadIcon sx={{ fontSize: 24 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Upload File
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Upload Area - Only show when no file is selected */}
          {uploadedFiles.length === 0 && (
            <Paper
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              sx={{
                border: dragActive ? '3px dashed #667eea' : '2px dashed #e0e0e0',
                borderRadius: 3,
                p: 3,
                marginTop: 5,
                textAlign: 'center',
                backgroundColor: dragActive ? '#f8f9ff' : '#fafafa',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: '#667eea',
                  backgroundColor: '#f8f9ff'
                }
              }}
              onClick={handleFileSelect}
            >
              <CloudUploadIcon sx={{ fontSize: 48, color: '#667eea', mb: 1.5 }} />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                {dragActive ? 'Drop file here' : 'Drag & Drop file here'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                or click to browse file
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 2 }}>
                <span style={{ color: '#000000' }}>Supported File Types: </span>
                <span style={{ color: '#e53935' }}>.pdf</span>,{' '}
                <span style={{ color: '#1e88e5' }}>.jpg</span>,{' '}
                <span style={{ color: '#f74a06ff' }}>.jpeg</span>,{' '}
                <span style={{ color: '#43a047' }}>.png</span>
              </Typography>
              <Button
                variant="outlined"
                startIcon={<FileUploadIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleFileSelect();
                }}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  borderColor: '#667eea',
                  color: '#667eea',
                  px: 3,
                  py: 1,
                  '&:hover': {
                    borderColor: '#5a6fd8',
                    backgroundColor: '#f8f9ff'
                  }
                }}
              >
                Select File
              </Button>
            </Paper>
          )}

          {/* Hidden file inputs */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />

          {/* Selected File */}
          {uploadedFiles.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Selected File
              </Typography>
              <Paper
                sx={{
                  p: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderRadius: 2,
                  border: '1px solid #e0e0e0',
                  backgroundColor: '#f8f9ff'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FileUploadIcon sx={{ color: '#667eea' }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {uploadedFiles[0].name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(uploadedFiles[0].size)}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  onClick={() => removeFile(uploadedFiles[0].id)}
                  size="small"
                  sx={{ color: '#f44336' }}
                >
                  <CloseIcon />
                </IconButton>
              </Paper>
            </Box>
          )}

          {/* Upload Progress */}
          {uploading && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Uploading file...
              </Typography>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3,
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
                  }
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {uploadProgress}% complete
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={handleClose}
            sx={{
              textTransform: 'none',
              color: '#666',
              '&:hover': { backgroundColor: '#f5f5f5' }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={uploadedFiles.length === 0 || uploading}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              textTransform: 'none',
              borderRadius: 2,
              px: 3,
              py: 1,
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)'
              },
              '&:disabled': {
                background: '#e0e0e0',
                color: '#999'
              }
            }}
          >
            {uploading ? 'Uploading...' : 'Upload File'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Popup Dialog */}
      <Dialog
        open={uploadSuccess}
        onClose={() => { }} // Prevent closing on outside click
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={true} // Prevent closing with Escape key
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            overflow: 'hidden',
            maxWidth: '500px'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 2.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 24 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Upload Successful!
            </Typography>
          </Box>
          <IconButton
            onClick={() => { }} // Disable close button
            sx={{ color: 'white', opacity: 0.5, cursor: 'not-allowed', '&:hover': { backgroundColor: 'transparent' } }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 48, color: '#4caf50', mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: '#2e7d32' }}>
              File Uploaded Successfully!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your file has been uploaded and is being processed.
            </Typography>
          </Box>

          {uploadResponseData && (
            <Paper sx={{ p: 1.5, backgroundColor: '#f8f9ff', borderRadius: 2, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: '#1976d2' }}>
                Upload Details
              </Typography>

              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#666', fontSize: '0.85rem' }}>
                      File Name:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                      {uploadResponseData.uploaded_file}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#666', fontSize: '0.85rem' }}>
                      Task ID:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {uploadResponseData.task_id}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#666', fontSize: '0.85rem' }}>
                      Slug:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {uploadResponseData.slug}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          )}

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.85rem' }}>
              Your file is now being processed by our AI agent. You can track the progress in your dashboard.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 0, justifyContent: 'center' }}>
          <Button
            onClick={() => handleStartStatusPolling(uploadResponseData.task_id)}
            variant="contained"
            size="large"
            sx={{
              background: 'linear-gradient(135deg, #3B5CFF 0%, #2E4BD8 100%)',
              textTransform: 'none',
              borderRadius: 2,
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #2E4BD8 0%, #1E3BC8 100%)'
              }
            }}
          >
            Start Processing
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UploadPopup;
